import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import referralService from '@/services/referralService'
import commissionService from '@/services/commissionService'
import PageContainer from '@/components/common/PageContainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const statusVariants = {
  clicked: 'secondary',
  registered: 'default',
  trial_started: 'warning',
  trial_converted: 'success',
  active: 'success',
  churned: 'danger',
}

const statusBgClasses = {
  clicked: 'bg-neutral-400 dark:bg-neutral-600',
  registered: 'bg-info',
  trial_started: 'bg-warning',
  trial_converted: 'bg-success',
  active: 'bg-success',
  churned: 'bg-danger',
}

const statusLabels = {
  clicked: 'Click',
  registered: 'Registrado',
  trial_started: 'En Trial',
  trial_converted: 'Convertido',
  active: 'Activo',
  churned: 'Cancelado',
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-caption text-subtle uppercase tracking-wide">{label}</p>
      <div className="text-body text-foreground mt-0.5 break-words">{children}</div>
    </div>
  )
}

export default function ReferralDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [referral, setReferral] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        const data = await referralService.getReferralById(id)
        setReferral(data.data)
      } catch {
        toast.error('Error al cargar el referido')
        navigate('/referrals')
      } finally {
        setLoading(false)
      }
    })()
  }, [id, navigate])

  const getTimelineEvents = () => {
    if (!referral?.conversion) return []
    const events = []
    const c = referral.conversion
    if (c.clickedAt) events.push({ label: 'Click en enlace', date: c.clickedAt, status: 'clicked' })
    if (c.registeredAt)
      events.push({ label: 'Usuario registrado', date: c.registeredAt, status: 'registered' })
    if (c.trialStartedAt)
      events.push({ label: 'Inicio de trial', date: c.trialStartedAt, status: 'trial_started' })
    if (c.convertedAt)
      events.push({ label: 'Conversión a pago', date: c.convertedAt, status: 'trial_converted' })
    if (c.churnedAt) events.push({ label: 'Cancelación', date: c.churnedAt, status: 'churned' })
    return events.sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  if (loading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-72" />
      </PageContainer>
    )
  }

  if (!referral) {
    return (
      <PageContainer>
        <p className="text-body text-subtle">Referido no encontrado</p>
      </PageContainer>
    )
  }

  const timeline = getTimelineEvents()
  const fullName =
    `${referral.referredUser?.firstName || ''} ${referral.referredUser?.lastName || ''}`.trim()

  return (
    <PageContainer>
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/referrals')}
          aria-label="Volver"
        >
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-h1 font-semibold text-foreground leading-tight">
            {fullName || 'Detalle del referido'}
          </h2>
          <p className="text-body-sm text-subtle truncate">
            {referral.referredUser?.email}
          </p>
        </div>
        <Badge variant={statusVariants[referral.status] || 'secondary'} className="shrink-0">
          {statusLabels[referral.status] || referral.status}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Información del usuario</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre">{fullName || '—'}</Field>
            <Field label="Email">{referral.referredUser?.email || '—'}</Field>
            <Field label="Fecha de registro">
              {referral.referredUser?.registeredAt
                ? new Date(referral.referredUser.registeredAt).toLocaleDateString('es-ES')
                : '—'}
            </Field>
            <Field label="Código de referido">
              <code className="font-mono break-all">{referral.referralCode}</code>
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Suscripción</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Field label="Plan">{referral.subscription?.plan || '—'}</Field>
            <Field label="Precio mensual">
              {referral.subscription?.monthlyPrice
                ? commissionService.formatCurrency(referral.subscription.monthlyPrice)
                : '—'}
            </Field>
            <Field label="Estado">
              <Badge
                variant={referral.subscription?.status === 'active' ? 'success' : 'secondary'}
              >
                {referral.subscription?.status || 'N/A'}
              </Badge>
            </Field>
            <Field label="Comisión activa">
              <Badge variant={referral.isCommissionActive ? 'success' : 'secondary'}>
                {referral.isCommissionActive ? 'Sí' : 'No'}
              </Badge>
            </Field>
          </CardContent>
        </Card>
      </div>

      {referral.cd2b && (
        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Cuenta cd-2b</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <Field label="Empresa">{referral.cd2b.empresaNombre || '—'}</Field>
            <Field label="Edición">{referral.cd2b.edicion || '—'}</Field>
            <Field label="Giro">{referral.cd2b.giro || '—'}</Field>
            <Field label="Modalidad">{referral.cd2b.modalidad || '—'}</Field>
            <Field label="Facturación mensual">
              {typeof referral.cd2b.facturacionMensual === 'number'
                ? commissionService.formatCurrency(referral.cd2b.facturacionMensual)
                : '—'}
            </Field>
            <Field label="Estado instancia">
              {referral.cd2b.estadoInstancia ? (
                <Badge variant={referral.cd2b.estadoInstancia === 'activa' ? 'success' : 'secondary'}>
                  {referral.cd2b.estadoInstancia}
                </Badge>
              ) : (
                '—'
              )}
            </Field>
            <Field label="Estado suscripción">
              <Badge variant={referral.cd2b.subStatus === 'active' ? 'success' : 'secondary'}>
                {referral.cd2b.subStatus || '—'}
              </Badge>
            </Field>
            <Field label="Cotización">
              {referral.cd2b.propuestaId ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/consultor/cotizaciones/${referral.cd2b.propuestaId}`)}
                >
                  Ver cotización
                </Button>
              ) : (
                '—'
              )}
            </Field>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Historial de conversión</CardTitle>
        </CardHeader>
        <CardContent>
          {timeline.length === 0 ? (
            <p className="text-body-sm text-subtle text-center py-4">
              No hay eventos registrados
            </p>
          ) : (
            <ol className="relative">
              {timeline.map((event, idx) => (
                <li key={idx} className="flex gap-3 pb-4 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        'h-3 w-3 rounded-full ring-4 ring-card shadow-sm shrink-0',
                        statusBgClasses[event.status] || 'bg-neutral-400 dark:bg-neutral-600'
                      )}
                    />
                    {idx < timeline.length - 1 && (
                      <span className="w-0.5 flex-1 bg-line mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pt-0.5 -mt-0.5">
                    <p className="text-body-sm font-medium text-foreground">{event.label}</p>
                    <p className="text-caption text-subtle mt-0.5">
                      {new Date(event.date).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {referral.attribution &&
        Object.keys(referral.attribution).some((k) => referral.attribution[k]) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-h3">Atribución</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 grid-cols-2 md:grid-cols-4">
              {referral.attribution.source && (
                <Field label="Fuente">{referral.attribution.source}</Field>
              )}
              {referral.attribution.medium && (
                <Field label="Medio">{referral.attribution.medium}</Field>
              )}
              {referral.attribution.campaign && (
                <Field label="Campaña">{referral.attribution.campaign}</Field>
              )}
              {referral.attribution.landingPage && (
                <Field label="Landing page">
                  <span className="break-all">{referral.attribution.landingPage}</span>
                </Field>
              )}
            </CardContent>
          </Card>
        )}
    </PageContainer>
  )
}
